import React from 'react';
import { MMMConfigFormState } from '../../types/mmmConfig';
import { Checkbox } from '../FormFields';

interface FeatureEngineeringSectionProps {
  config: MMMConfigFormState;
  onChange: (config: Partial<MMMConfigFormState>) => void;
  errors: Record<string, string | undefined>;
}

export const FeatureEngineeringSection: React.FC<FeatureEngineeringSectionProps> = ({
  config,
  onChange,

}) => {
  const feEngineering = config.feature_engineering || {
    enabled: false,
    use_lagged_features: false,
    use_interaction_terms: false,
    lag_periods: [1, 2, 3],
  };

  const handleToggleEnabled = (enabled: boolean) => {
    onChange({
      feature_engineering: { ...feEngineering, enabled },
    });
  };

  const handleToggleLagged = (enabled: boolean) => {
    onChange({
      feature_engineering: { ...feEngineering, use_lagged_features: enabled },
    });
  };

  const handleToggleInteractions = (enabled: boolean) => {
    onChange({
      feature_engineering: { ...feEngineering, use_interaction_terms: enabled },
    });
  };

  const handleLagPeriodsChange = (val: string) => {
    try {
      const periods = val.split(',').map((p) => parseInt(p.trim())).filter((p) => !isNaN(p));
      onChange({
        feature_engineering: { ...feEngineering, lag_periods: periods },
      });
    } catch (e) {
      // Ignore parse errors
    }
  };

  return (
    <div className="card">
      <h2 className="text-xl font-bold mb-6 text-slate-800 tracking-tight">Feature Engineering</h2>

      <div className="space-y-4">
        <Checkbox
          name="fe_enabled"
          label="Enable Feature Engineering"
          value={feEngineering.enabled}
          onChange={handleToggleEnabled}
          helperText="Advanced feature creation like lagged variables and interactions"
        />

        {feEngineering.enabled && (
          <>
            <div className="ml-6 space-y-4 pt-4 border-l-2 border-indigo-300 pl-5">
              <Checkbox
                name="use_lagged"
                label="Use Lagged Features"
                value={feEngineering.use_lagged_features || false}
                onChange={handleToggleLagged}
                helperText="Include previous time periods as features (e.g., spend from last week)"
              />

              {feEngineering.use_lagged_features && (
                <div className="ml-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Lag Periods (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={feEngineering.lag_periods?.join(', ') || '1, 2, 3'}
                    onChange={(e) => handleLagPeriodsChange(e.target.value)}
                    placeholder="1, 2, 3, 4"
                    className="w-full px-4 py-2.5 rounded-lg border-2 bg-white text-slate-900 text-sm font-medium placeholder:text-slate-400 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 border-slate-200 hover:border-slate-300 focus:border-indigo-500"
                  />
                  <p className="text-slate-500 text-xs mt-2">
                    Example: 1, 2, 3 creates features for previous 1, 2, and 3 periods
                  </p>
                </div>
              )}

              <Checkbox
                name="use_interactions"
                label="Use Interaction Terms"
                value={feEngineering.use_interaction_terms || false}
                onChange={handleToggleInteractions}
                helperText="Model synergies between channels (multiplicative effects)"
              />
            </div>
          </>
        )}
      </div>

      <div className="mt-8 p-5 bg-gradient-to-br from-indigo-50/80 to-blue-50/60 rounded-xl border border-indigo-200/60">
        <h3 className="text-sm font-bold text-indigo-900 mb-3 tracking-tight">Feature Engineering Options</h3>
        <ul className="text-sm text-indigo-800/90 space-y-2.5">
          <li className="flex gap-2">
            <span className="text-indigo-500 font-bold">•</span>
            <span><strong className="text-indigo-900">Lagged Features:</strong> Include historical values. Useful when ad spend affects sales over multiple periods.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-indigo-500 font-bold">•</span>
            <span><strong className="text-indigo-900">Interaction Terms:</strong> Model synergies between channels. Useful for joint marketing campaigns.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-indigo-500 font-bold">•</span>
            <span><strong className="text-indigo-900">Note:</strong> Increasing features increases computation time and risk of overfitting.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};
